import { useCallback } from 'react';
import { useRouter } from 'next/navigation';
import styled from 'styled-components';
import type { Asset } from '@frameio/graphql-client/generated';
import { Box, Text } from '@frameio/vapor';
import { useClientAssetFieldMatchInfo } from '../../../../../../hooks/useClientAssetFieldMatchInfo';
import { FieldMatch as SharedFieldMatch } from '../../../../../../shared/components';
import { useQuickSearchModal } from '../../../../../../shared/contexts';
import { useQuickSearchSupportedAssetMediaV2Fragment } from '../../../../../../shared/gql/generated/QuickSearchSupportedAssetMediaV2';
import type {
  AssetResultMatch,
  OnMatchClickHandler,
} from '../../../../../../shared/types';
import {
  getAssetResultItemLink,
  getMatchLabel,
} from '../../../../../../shared/utils';
import { InfoListRow } from '../../shared';

type FieldMatchProps = {
  assetId: string;
  match: AssetResultMatch;
  onMatchClick?: OnMatchClickHandler;
};

export const FieldMatch = ({
  assetId,
  match,
  onMatchClick,
}: FieldMatchProps) => {
  const router = useRouter();
  const { closeModal } = useQuickSearchModal();

  const matchType = match?.__typename;

  const { data: assetData } =
    useQuickSearchSupportedAssetMediaV2Fragment(assetId);

  const matchLabel = getMatchLabel(match);

  const { setClientAssetFieldMatchInfo } = useClientAssetFieldMatchInfo();

  const handleOnClick = useCallback(() => {
    if (matchLabel) {
      setClientAssetFieldMatchInfo({
        id: assetId,
        fieldName: matchLabel,
      });
    }

    onMatchClick?.(matchType);

    const href = getAssetResultItemLink(assetData as Asset);
    router.push(href);

    closeModal({ navigatingTo: href });
  }, [
    assetData,
    assetId,
    closeModal,
    matchLabel,
    matchType,
    onMatchClick,
    router,
    setClientAssetFieldMatchInfo,
  ]);

  return (
    <button
      onClick={handleOnClick}
      style={{
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: 0,
        flex: 1,
        display: 'flex',
        justifyContent: 'space-between',
        flexDirection: 'row',
        textAlign: 'left',
      }}
    >
      <InfoListRow>
        <MatchTitleWrapper>
          <Box paddingY={4}>
            <Text color="primary" truncate={true} variant="secondary">
              {matchLabel}
            </Text>
          </Box>
        </MatchTitleWrapper>
        <MatchValueWrapper>
          <SharedFieldMatch
            fieldMatch={match}
            truncate={false}
            variant="block"
          />
        </MatchValueWrapper>
      </InfoListRow>
    </button>
  );
};

const MatchTitleWrapper = styled.div`
  flex: 0 0 42%;
  min-width: 0;
`;

const MatchValueWrapper = styled.div`
  flex: 0 1 auto;
  min-width: 0;
  text-align: right;
`;
